/**
 * Clamp a wall-clock-derived seek target (seconds into a camera's media
 * timeline) to a position the element can actually seek to.
 *
 * A resume point saved on an earlier visit can fall OUTSIDE the media that
 * is currently available: a live rolling window may have evicted it off the
 * back (target < buffer start), or a stale/extreme UTC can land past the
 * end. A bare `Math.max(0, target)` puts those cases at buffer start
 * (currentTime 0 — the OLDEST buffered segment, not near live) or lets the
 * seek run past the end. This clamps to the real `seekable` range (falling
 * back to `[0, duration]`).
 *
 * `intent` distinguishes a restored position from a target the user just
 * picked. `resume` snaps anything outside the live window to the live edge —
 * right for a saved position that rolled off the back of a live buffer, wrong
 * for a click on the scrubber: that must land where the user clicked, even
 * if it's behind the current window, or scrubbing backward on a live stream
 * would be impossible. `explicit` only clamps toward the live edge when the
 * target is genuinely past it.
 */

/**
 * Structural shape of the bits of `HTMLVideoElement` we read, so this stays
 * unit-testable without a DOM. `HTMLVideoElement` is assignable to it.
 */
export interface SeekableMedia {
  duration: number;
  seekable: { length: number; start(index: number): number; end(index: number): number };
}

/** Stay this far behind the live edge so the player keeps a forward buffer. */
export const LIVE_EDGE_BACKOFF_S = 3;

export function clampSeekSeconds(
  video: SeekableMedia,
  targetSec: number,
  opts: { live: boolean; intent: 'explicit' | 'resume' },
): number {
  let lo = 0;
  let hi = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Infinity;
  const sk = video.seekable;
  if (sk && sk.length > 0) {
    lo = sk.start(0);
    hi = sk.end(sk.length - 1);
  }

  if (opts.live && Number.isFinite(hi)) {
    const liveEdge = Math.max(lo, hi - LIVE_EDGE_BACKOFF_S);
    if (!Number.isFinite(targetSec)) return liveEdge;
    if (opts.intent === 'resume') {
      // Anywhere OUTSIDE the window — rolled off the back, or at/past the
      // edge — resumes near the live edge rather than at the oldest
      // buffered segment. An in-window target is honored as-is.
      if (targetSec < lo || targetSec > liveEdge) return liveEdge;
      return targetSec;
    }
    return Math.min(Math.max(targetSec, lo), liveEdge);
  }

  // VOD (or no known upper bound yet): clamp into [lo, hi].
  if (!Number.isFinite(targetSec)) return lo;
  if (!Number.isFinite(hi)) return Math.max(lo, targetSec);
  return Math.min(Math.max(targetSec, lo), hi);
}
